import React, { useRef, useState, useEffect, useCallback } from "react";
import Cropper from "react-easy-crop";
import Modal from "./Modal";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, RotateCcw, Keyboard, ScanLine, Check, ArrowRight, Layers, Crop as CropIcon, RotateCw, Wand2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toHref } from "../lib/links";
import { downscaleImage, getCroppedFile } from "../lib/image";

function mergeExtracted(a, b) {
  const out = { ...a };
  ["name", "title", "company", "phone", "email", "website", "industry", "address"].forEach((f) => {
    if (!out[f] && b[f]) out[f] = b[f];
  });
  const seen = new Map();
  [...(a.social_links || []), ...(b.social_links || [])].forEach((s) => {
    const key = toHref(s).toLowerCase();
    if (s && !seen.has(key)) seen.set(key, s);
  });
  out.social_links = Array.from(seen.values());
  if (!out.phone2 && b.phone && b.phone !== out.phone) out.phone2 = b.phone;
  if (!out.email2 && b.email && b.email !== out.email) out.email2 = b.email;
  out.image_path = a.image_path || b.image_path || "";
  if (b.image_path && b.image_path !== a.image_path) out.back_image_path = b.image_path;
  return out;
}

export default function ScannerModal({ open, onClose, onExtracted, onManual }) {
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [raw, setRaw] = useState(null); // { url } image awaiting crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [enhance, setEnhance] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [side, setSide] = useState("front"); // front | back
  const [stage, setStage] = useState("capture"); // capture | choice
  const [merged, setMerged] = useState(null);

  const onCropComplete = useCallback((_, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const clearRaw = useCallback(() => {
    setRaw((r) => {
      if (r?.url) URL.revokeObjectURL(r.url);
      return null;
    });
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setEnhance(false);
    setCroppedAreaPixels(null);
  }, []);

  useEffect(() => {
    if (!open) {
      setPreview((p) => {
        if (p?.url) URL.revokeObjectURL(p.url);
        return null;
      });
      clearRaw();
      setScanning(false);
      setSide("front");
      setStage("capture");
      setMerged(null);
    }
  }, [open, clearRaw]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    const shrunk = await downscaleImage(file);
    clearRaw();
    setRaw({ url: URL.createObjectURL(shrunk) });
  };

  const applyCrop = async () => {
    if (!raw?.url || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedFile(raw.url, croppedAreaPixels, `card-${side}.jpg`, { rotation, enhance });
      if (preview?.url) URL.revokeObjectURL(preview.url);
      setPreview({ blob: cropped, url: URL.createObjectURL(cropped) });
      clearRaw();
    } catch {
      toast.error("Could not crop image");
    }
  };

  const retake = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    clearRaw();
  };

  const scan = async () => {
    if (!preview) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.blob, `card-${side}.jpg`);
      const { data } = await api.post("/cards/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const combined = merged ? mergeExtracted(merged, data) : { ...data };
      setMerged(combined);
      if (preview.url) URL.revokeObjectURL(preview.url);
      setPreview(null);
      if (side === "front") {
        setStage("choice");
      } else {
        onExtracted(combined);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const scanBack = () => {
    setSide("back");
    setStage("capture");
  };

  // ---- CHOICE screen (after front scan) ----
  if (stage === "choice" && merged) {
    return (
      <Modal open={open} onClose={onClose} maxWidth="max-w-lg" testId="scanner-modal">
        <div className="p-6">
          <div className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/15 text-emerald-400 px-2.5 py-1 rounded-full font-medium mb-3">
            <Check className="w-3.5 h-3.5" /> Front captured
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">
            {merged.name || merged.company || "Card scanned"}
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Many cards print extra details on the back — socials, a second number, an address. Scan it
            and we'll merge both sides into one contact.
          </p>

          <button
            data-testid="scan-back-button"
            onClick={scanBack}
            className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-colors mb-3"
          >
            <Layers className="w-5 h-5" /> Scan the back
          </button>
          <button
            data-testid="skip-to-review-button"
            onClick={() => onExtracted(merged)}
            className="w-full py-3.5 rounded-xl border border-slate-700 text-slate-200 hover:border-slate-500 font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            Skip — review &amp; save <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Modal>
    );
  }

  // ---- CAPTURE screen ----
  const isBack = side === "back";
  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-lg" testId="scanner-modal">
      <div className="p-6">
        <p className="overline text-blue-500 mb-1">
          {isBack ? "Scan the back" : "Scan a business card"}
        </p>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-5">
          {isBack ? "Flip it over, snap the back." : "Point, snap, done."}
        </h2>

        <div
          data-testid="camera-viewfinder"
          className="relative aspect-[1.6/1] w-full rounded-2xl overflow-hidden bg-[#0B0F17] border border-slate-800 mb-4"
        >
          {raw ? (
            <Cropper
              image={raw.url}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1.6}
              minZoom={1}
              maxZoom={4}
              zoomSpeed={0.2}
              restrictPosition
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              objectFit="contain"
              style={{ mediaStyle: { filter: enhance ? "brightness(1.2) contrast(1.12) saturate(1.05)" : "none" } }}
            />
          ) : preview ? (
            <img src={preview.url} alt="Captured card" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 gap-3 dotted-bg px-8 text-center">
              <div className="absolute inset-6 border-2 border-blue-400/40 rounded-lg pointer-events-none" />
              <ScanLine className="w-10 h-10 text-slate-600" />
              <p className="text-sm">
                Tap <span className="text-slate-300 font-medium">Take photo</span> to open your camera,
                or upload a photo of the card.
              </p>
            </div>
          )}

          {scanning && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
              <div className="scan-laser" />
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <p className="text-sm text-blue-300 font-mono">Reading card…</p>
            </div>
          )}
        </div>

        {raw && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <button
                data-testid="crop-rotate-button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-700 text-slate-200 hover:border-blue-500 text-xs font-semibold transition-colors"
              >
                <RotateCw className="w-4 h-4" /> Rotate
              </button>
              <button
                data-testid="crop-enhance-button"
                onClick={() => setEnhance((v) => !v)}
                aria-pressed={enhance}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                  enhance
                    ? "border-blue-500 bg-blue-500/15 text-blue-300"
                    : "border-slate-700 text-slate-200 hover:border-blue-500"
                }`}
              >
                <Wand2 className="w-4 h-4" /> Auto-brighten{enhance ? " · on" : ""}
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400 mb-2">
              <CropIcon className="w-3.5 h-3.5 text-blue-500" /> Drag to reposition · pinch or use the slider to zoom
            </label>
            <input
              data-testid="crop-zoom-slider"
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-blue-500 cursor-pointer"
            />
          </div>
        )}

        {/* Native camera (opens the phone camera on mobile) */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFile}
          className="hidden"
          data-testid="file-input"
        />
        {/* Photo library / file picker (no capture attribute) */}
        <input
          ref={libraryRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid="library-file-input"
        />

        {raw ? (
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="crop-retake-button"
              onClick={retake}
              className="py-3 rounded-xl border border-slate-700 text-slate-200 hover:border-slate-500 font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <RotateCcw className="w-5 h-5" /> Retake
            </button>
            <button
              data-testid="crop-confirm-button"
              onClick={applyCrop}
              className="py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <Check className="w-5 h-5" /> Use photo
            </button>
          </div>
        ) : !preview ? (
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="capture-photo-button"
              onClick={() => cameraRef.current?.click()}
              className="py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <Camera className="w-5 h-5" /> Take photo
            </button>
            <button
              data-testid="upload-photo-button"
              onClick={() => libraryRef.current?.click()}
              className="py-3 rounded-xl border border-slate-700 text-slate-200 hover:border-blue-500 font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <Upload className="w-5 h-5" /> Upload
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={retake}
              disabled={scanning}
              className="py-3 rounded-xl border border-slate-700 text-slate-200 hover:border-slate-500 font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <RotateCcw className="w-5 h-5" /> Retake
            </button>
            <button
              data-testid="scan-extract-button"
              onClick={scan}
              disabled={scanning}
              className="py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {scanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ScanLine className="w-5 h-5" /> Extract with AI</>}
            </button>
          </div>
        )}

        {isBack && !preview && !raw && (
          <button
            data-testid="skip-back-button"
            onClick={() => onExtracted(merged)}
            className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            Skip the back — review &amp; save
          </button>
        )}

        {!isBack && !raw && (
          <button
            data-testid="add-manually-button"
            onClick={onManual}
            className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            <Keyboard className="w-4 h-4" /> Enter details manually
          </button>
        )}
      </div>
    </Modal>
  );
}
